import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Plus, Loader2, KeyRound, Eye, EyeOff } from 'lucide-react';
import {
  TeamSecret,
  SecretCreate,
  listSecrets,
  createSecret,
  deleteSecret,
} from '@/services/secretsApi';

interface SecretsTabProps {
  className?: string;
}

export function SecretsTab({ className }: SecretsTabProps) {
  const { toast } = useToast();
  const [secrets, setSecrets] = useState<TeamSecret[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValue, setShowValue] = useState(false);

  // Form state
  const [formData, setFormData] = useState<SecretCreate>({
    name: '',
    value: '',
    description: '',
  });

  const loadSecrets = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await listSecrets();
      setSecrets(data);
    } catch (error) {
      console.error('Failed to load secrets:', error);
      toast({
        title: 'Error',
        description: 'Failed to load secrets',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadSecrets();
  }, [loadSecrets]);

  const handleCreate = async () => {
    if (!formData.name?.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Secret name is required',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.value?.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Secret value is required',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createSecret(formData);
      setShowCreateDialog(false);
      setFormData({ name: '', value: '', description: '' });
      setShowValue(false);
      await loadSecrets();
      toast({
        title: 'Success',
        description: 'Secret created successfully',
      });
    } catch (error: any) {
      console.error('Failed to create secret:', error);
      const detail = error?.response?.data?.detail || 'Failed to create secret';
      toast({
        title: 'Error',
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (name: string) => {
    if (!confirm(`Are you sure you want to delete the secret "${name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await deleteSecret(name);
      await loadSecrets();
      toast({
        title: 'Success',
        description: 'Secret deleted',
      });
    } catch (error) {
      console.error('Failed to delete secret:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete secret',
        variant: 'destructive',
      });
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className={className}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5" />
                Team Secrets
              </CardTitle>
              <CardDescription>
                Manage secrets accessible from notebooks via <code className="text-xs bg-muted px-1 rounded">cap.secrets.get("NAME")</code>
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Secret
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : secrets.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <KeyRound className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No secrets configured</p>
              <p className="text-sm mt-1">Add secrets to use in notebooks</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {secrets.map((secret) => (
                  <TableRow key={secret.id}>
                    <TableCell className="font-medium font-mono">
                      {secret.name}
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        {secret.key_hint}
                      </code>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {secret.description || '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(secret.created_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(secret.name)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
            <p className="font-medium mb-1">Usage in notebooks:</p>
            <code className="text-xs">
              {`api_key = await cap.secrets.get("SECRET_NAME")`}
            </code>
            <p className="mt-2 text-xs">
              Secrets must be whitelisted in notebook capabilities: <code>{`secrets: { allowed: ["SECRET_NAME"] }`}</code>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Create Secret Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Secret</DialogTitle>
            <DialogDescription>
              Create a new secret for use in notebooks. Values are encrypted at rest.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="e.g., POLYGON_API_KEY"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Use UPPER_SNAKE_CASE (letters, numbers, underscores)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="value">Value *</Label>
              <div className="relative">
                <Input
                  id="value"
                  type={showValue ? 'text' : 'password'}
                  placeholder="Enter secret value"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowValue(!showValue)}
                >
                  {showValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setFormData({ name: '', value: '', description: '' });
              setShowValue(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Secret
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SecretsTab;
